import { type IncomingMessage, type ServerResponse } from "http"
import { deleteCookie, getCookie, setCookie } from "cookies-next"
import { type NextApiRequest, type NextApiResponse } from "next"
import { logger } from "@/lib/logger"
import { type Partner } from "../constants/partner"
import { type PartnerSessionType } from "../types/partnerSession"
import { getPartnerConfig } from "../utils/getPartnerConfig"
import { DefaultSessionSchema, getDefaultExpiration } from "./default"

export class PartnerSessionManager {
    private req: NextApiRequest | IncomingMessage
    private res: NextApiResponse | ServerResponse

    constructor(req: NextApiRequest | IncomingMessage, res: NextApiResponse | ServerResponse) {
        this.req = req
        this.res = res
    }

    static getCookieName(partner: Partner) {
        return `session_${partner}`
    }

    /**
     * Sets the session cookie for a specific partner using their specific validation and expiration.
     */
    setSession<TPartner extends Partner>(partner: TPartner, data: PartnerSessionType<TPartner>) {
        // Get Schema
        const partnerConfig = getPartnerConfig(partner)
        const schema = partnerConfig.session?.schema || DefaultSessionSchema

        // Validate Data
        const parsedData = schema.parse(data)

        // Get Expiration Date
        const getExpiration = partnerConfig.session?.getExpiration || getDefaultExpiration
        const expires = getExpiration()

        // Set Cookie
        void setCookie(PartnerSessionManager.getCookieName(partner), JSON.stringify(parsedData), {
            req: this.req,
            res: this.res,
            expires,
            httpOnly: true,
            secure: process.env.NODE_ENV === "production",
            sameSite: "lax",
            path: "/",
        })
    }

    async getSession<TPartner extends Partner>(
        partner: TPartner
    ): Promise<PartnerSessionType<TPartner> | null> {
        try {
            const cookieName = PartnerSessionManager.getCookieName(partner)
            const cookieValue = await getCookie(cookieName, { req: this.req, res: this.res })

            if (!cookieValue || typeof cookieValue !== "string") {
                return null
            }

            const data = JSON.parse(cookieValue)
            const partnerConfig = getPartnerConfig(partner)
            const schema = partnerConfig.session?.schema || DefaultSessionSchema

            return schema.parse(data) as PartnerSessionType<TPartner>
        } catch (error) {
            // If the session is invalid, clear it immediately
            await this.clearSession(partner)

            logger.error({
                error,
                meta: {
                    feature: "partners",
                    subFeature: "session",
                    partner,
                },
            })

            return null
        }
    }

    async clearSession(partner: Partner) {
        await deleteCookie(PartnerSessionManager.getCookieName(partner), {
            req: this.req,
            res: this.res,
            path: "/",
        })
    }
}
